// src/componentes/DeudaDetalle/DetalleDeuda.jsx
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
  writeBatch,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, listAll, deleteObject } from "firebase/storage";

import { db } from "../../Firebase/firebaseconfig";
import ModalPago from "../ModalPago";
import ModalPagoSemimasivo from "../ModalPagoSemimasivo";
import ModalRenegociacion from "../PlanDia/ModalRenegociacion";
import PagoEvidenciaModal from "../PagoEvidenciaModal";


import {
  generarEstadoCuentaPDF,
  generarConstanciaLiquidacionPDF,
  generarComprobantePagoPDF,
  generarTicketPagoPDF,
} from "../../helpers/reportesDeuda";

import { n, r2 } from "../../helpers/devolucion";
import { cancelarComisionesEnCascada } from "../../services/cancelarComisionesEnCascada";
import { getComisionesPorDeuda } from "../../services/comisionesService";
import { calcularPerfilCobranzaV2 } from "../../helpers/cobranzaInteligenteV2";
import { metodoPagoLabel, metodoPagoPayload } from "../../helpers/metodosPago";
import { toJsDate, formatFecha, safeRender, toLocalISODate, hoyLocalISO } from "../../helpers/fechas";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import { registrarAuditoria } from "../../helpers/auditoria";
import {
  actualizarPromesaPago,
  crearGestionCobranza,
  crearPromesaPago,
  escucharGestionesPorDeuda,
  escucharPromesasPorDeuda,
} from "../../services/cobranzaOperativaService";
// Sprint 3.B: lista de llamadas grabadas + player inline
import LlamadasDeuda from "./LlamadasDeuda";
import { isCallCenterUiEnabled } from "../../helpers/callCenterFlags";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DescriptionIcon from "@mui/icons-material/Description";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import EditIcon from "@mui/icons-material/Edit";
import {
  IconButton,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
} from "@mui/material";

import notify from "../common/notify";
import { dialogService } from "../common/dialogService";
import "./DetalleDeuda.css";

const money = (v) =>
  Number(v ?? 0).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// HOTFIX v1.1.4: devolver yyyy-mm-dd en zona LOCAL del browser.
// El truco `setMinutes(- getTimezoneOffset())` + toISOString era un workaround
// fragil para timezones positivos (Asia, Europa) pero en MX (UTC-6) cuando
// el timestamp original es local midnight = 06:00 UTC, el offset move la
// hora a 12:00 UTC mismo día → toISOString → correcto. Pero si timestamp
// es algo como UTC midnight (2026-06-04T00:00:00Z) entonces setMinutes
// sube a 06:00 UTC = 12:00 CDMX = "2026-06-04" en UTC slice. Inconsistente.
// Mejor usar getFullYear/getMonth/getDate directos.
const toDateOnly = (value) => {
  const date = value?.toDate?.() || (value instanceof Date ? value : value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const toTimestampFromDateOnly = (value) => {
  if (value?.toDate) return value;
  if (!value) return Timestamp.now();

  // HOTFIX v1.1.4: si es string "yyyy-mm-dd" lo parseamos como LOCAL
  // ANTES que toJsDate. toJsDate hace `new Date("yyyy-mm-dd")` que en
  // ES2015+ se interpreta como UTC midnight, lo cual en CDMX (UTC-6)
  // queda en "ayer 18:00 local". Despues setHours(0,0,0,0) hace que
  // se guarde como local midnight de AYER → off-by-one bug confirmado
  // en pagos editados (createdAt=hoy pero fecha=ayer).
  if (typeof value === "string") {
    const ymd = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      const [, y, m, d] = ymd;
      // Construir explicitamente en LOCAL (NO usar `new Date("yyyy-mm-dd")`).
      const local = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
      return Number.isNaN(local.getTime()) ? Timestamp.now() : Timestamp.fromDate(local);
    }
  }

  // Para otros formatos (Date, {seconds}, etc.) sí podemos usar toJsDate.
  const d = toJsDate(value);
  if (d) {
    d.setHours(0, 0, 0, 0);
    return Timestamp.fromDate(d);
  }
  // Fallback final: string raro, tratar como yyyy-mm-dd LOCAL.
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? Timestamp.now() : Timestamp.fromDate(date);
};

const getPagoDateMs = (value) => {
  const date = value?.toDate?.() || (value instanceof Date ? value : value ? new Date(value) : null);
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : Date.now();
};

const getPagoId = (pago, index) => String(pago?.pagoId || pago?.id || `idx-${index}`);

const getPagoCajaId = (pago = {}) =>
  pago?.cajaMovimientoId || pago?.cajaId || pago?.movimientoCajaId || "";

const calcularSaldoDeuda = (deuda, pagos = []) => {
  const monto = n(deuda?.monto ?? deuda?.importe);
  const enganche = n(deuda?.enganche);
  let totalPagado = 0;
  let engancheEnPagos = false;

  pagos.forEach((pago) => {
    const cantidad = n(pago?.cantidad);
    const tipo = String(pago?.tipo || "").toLowerCase();
    if (cantidad > 0) totalPagado += cantidad;
    if (tipo === "enganche" && cantidad > 0) engancheEnPagos = true;
  });

  if (!engancheEnPagos) totalPagado += enganche;
  return Math.max(monto - totalPagado, 0);
};

const formatDateOnlyMx = (value) => {
  const date = value?.toDate?.() || (value instanceof Date ? value : value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "-";
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const isDevolucionDeuda = (d = {}) => {
  const estado = String(d?.estado || "").toLowerCase().trim();
  const estatus = String(d?.estatus || "").toLowerCase().trim();

  return !!(
    d?.devolucion === true ||
    d?.esDevolucion === true ||
    estado === "en_devolucion" ||
    estado === "devuelta" ||
    estado === "cerrada_devolucion" ||
    estado.includes("devolucion") ||
    estatus.includes("devolucion")
  );
};

function computeFinanzas(d = {}) {
  const importeVenta = n(d.monto ?? d.importe ?? 0);
  const enganche = n(d.enganche);
  const pagosArr = Array.isArray(d.pagos) ? d.pagos : [];

  let positivos = 0;
  let devueltoCliente = 0;
  let ajustesDev = 0;
  let engancheEnPagosAcumulado = 0;
  let mensualidadesAcumulado = 0;

  for (const p of pagosArr) {
    const cant = n(p?.cantidad);
    const tipo = String(p?.tipo || "").toLowerCase();

    if (cant > 0) {
      positivos += cant;
      if (tipo === "enganche") engancheEnPagosAcumulado += cant;
      else if (tipo !== "ajuste_devolucion" && tipo !== "ajuste") mensualidadesAcumulado += cant;
    }

    if (cant < 0) {
      if (tipo === "devolucion_pago") devueltoCliente += Math.abs(cant);
      if (tipo === "ajuste_devolucion" || tipo === "ajuste") ajustesDev += Math.abs(cant);
    }
  }

  const engEnPagos = engancheEnPagosAcumulado > 0;

  // Sprint ENG-FIX (backwards-compat): tri-estado de engancheCobradoFuera.
  //   true       → cobrado fuera explícito (suma)
  //   false      → pendiente explícito (no suma)
  //   undefined  → legacy (suma; comportamiento previo). El admin migra con los
  //                botones del KPI.
  // Si hay pago marcado tipo:"enganche", ya está incluido en `positivos`.
  const engancheCobradoFuera = d?.engancheCobradoFuera === true;
  const engancheMarcadoPendiente = d?.engancheCobradoFuera === false;
  const engancheLegacy = d?.engancheCobradoFuera === undefined || d?.engancheCobradoFuera === null;
  const engancheCuenta = d?.engancheCobradoFuera !== false; // true o undefined
  const engancheCuentaEnTotal = engEnPagos || engancheCuenta;

  // Sprint ENG-2: separamos enganche cobrado vs mensualidades para mostrarlo
  // en el resumen y en el PDF.
  const engancheCobrado = engEnPagos
    ? engancheEnPagosAcumulado
    : (engancheCuenta ? enganche : 0);
  const mensualidadesCobradas = mensualidadesAcumulado;

  const sumaEngancheFuera = !engEnPagos && engancheCuenta ? enganche : 0;
  const totalPagadoVenta = positivos + sumaEngancheFuera;
  const saldoVenta = Math.max(importeVenta - totalPagadoVenta, 0);

  const comisionCampo = n(d.comisionCancelacion) || n(d?.devolucion?.comision);
  const comision = comisionCampo > 0 ? comisionCampo : ajustesDev;

  const abonadoTotal = positivos + sumaEngancheFuera;
  const baseDev = Math.max(abonadoTotal - comision, 0);
  const pendienteDev = Math.max(baseDev - devueltoCliente, 0);

  return {
    importeVenta,
    enganche,
    engancheCobrado,
    mensualidadesCobradas,
    engancheCobradoFuera,
    engancheMarcadoPendiente,
    engancheLegacy,
    engancheCuentaEnTotal,
    totalPagadoVenta,
    saldoVenta,
    baseDev,
    totalDevuelto: devueltoCliente,
    pendienteDev,
    comision,
    abonadoTotal,
    engEnPagos,
  };
}

const liberarPropiedadesDeDeuda = async (deudaActual) => {
  if (Array.isArray(deudaActual?.propiedades) && deudaActual.propiedades.length) {
    const updates = deudaActual.propiedades.map(async (p) => {
      if (!p.propiedadId) return;

      const pRef = doc(db, "propiedades", p.propiedadId);
      await updateDoc(pRef, {
        estado: "disponible",
        disponible: true,
        asociadaADeuda: false,
      });
    });

    await Promise.all(updates);
    return;
  }

  if (deudaActual?.propiedadId) {
    const pRef = doc(db, "propiedades", deudaActual.propiedadId);
    await updateDoc(pRef, {
      estado: "disponible",
      disponible: true,
      asociadaADeuda: false,
    });
    return;
  }

  if (deudaActual?.manzana && deudaActual?.lote) {
    const qProps = query(
      collection(db, "propiedades"),
      where("manzana", "==", deudaActual.manzana),
      where("lote", "==", deudaActual.lote)
    );

    const snap = await getDocs(qProps);

    const updates = snap.docs.map((dSnap) =>
      updateDoc(dSnap.ref, {
        estado: "disponible",
        disponible: true,
        asociadaADeuda: false,
      })
    );

    await Promise.all(updates);
  }
};

const DetalleDeuda = () => {
  const { id: clienteId, deudaId } = useParams();
  const navigate = useNavigate();
  const { can } = useUserContext();

  const [openSemiBulk, setOpenSemiBulk] = useState(false);
  const [deuda, setDeuda] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [responsable, setResponsable] = useState(null);
  const [cajaMovimientosPorPago, setCajaMovimientosPorPago] = useState(new Map());
  const [modalPago, setModalPago] = useState({ abierto: false, index: 0 });
  const [modalRenegAbierto, setModalRenegAbierto] = useState(false);
  const [constanciaOpen, setConstanciaOpen] = useState(false);
  const [constanciaFecha, setConstanciaFecha] = useState("");
  const [constanciaTrato, setConstanciaTrato] = useState("C.");
  const [loading, setLoading] = useState(true);
  const [showEvidPagoId, setShowEvidPagoId] = useState(null);
  const [nextDueStr, setNextDueStr] = useState("");

  const [openDev, setOpenDev] = useState(false);
  const [gastosAdmin, setGastosAdmin] = useState(0);
  const [comisionVenta, setComisionVenta] = useState(0);
  const [motivoDevolucion, setMotivoDevolucion] = useState("");

  const [archivos, setArchivos] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const inputRef = useRef();

  // Comisiones vinculadas a esta deuda (vendedor / comisionista)
  const [comisionesDeuda, setComisionesDeuda] = useState([]);

  const tiposImagen = ["png", "jpg", "jpeg", "gif", "webp", "bmp"];
  const tiposPDF = ["pdf"];

  const [escrituracionStatus, setEscrituracionStatus] = useState("");
  const [showProps, setShowProps] = useState(false);
  const puedeVerDeudas = can("deudas.ver");
  const puedeEditarDeuda = can("deudas.editar");
  const puedeDesactivarDeuda = can("deudas.desactivar");
  const puedeIniciarDevolucion = can("deudas.iniciarDevolucion");
  const puedeRegistrarPago = can("deudas.registrarPago");
  const puedeEditarPago = can("deudas.editarPago");
  const puedeEliminarPago = can("deudas.eliminarPago");
  const puedeDescargarPdf = can("deudas.descargarPDF");
  const puedeVerPromesas = can("cobranza.promesas.ver");
  const puedeCrearPromesas = can("cobranza.promesas.crear");
  const puedeEditarPromesas = can("cobranza.promesas.editar");
  const puedeVerGestiones = can("cobranza.gestiones.ver");
  const puedeCrearGestiones = can("cobranza.gestiones.crear");
  const [promesasPago, setPromesasPago] = useState([]);
  const [gestionesCobranza, setGestionesCobranza] = useState([]);
  const [savingOperativo, setSavingOperativo] = useState(false);
  const [promesaForm, setPromesaForm] = useState({
    montoPrometido: "",
    fechaPromesa: "",
    notas: "",
  });
  const [promesaEditandoId, setPromesaEditandoId] = useState(null);
  const [gestionForm, setGestionForm] = useState({
    tipo: "llamada",
    resultado: "",
    comentario: "",
  });
  const escrituracionOpciones = useMemo(
    () => [
      { value: "en_proceso", label: "En proceso" },
      { value: "no_escriturado", label: "No escriturado" },
      { value: "escriturado", label: "Escriturado" },
    ],
    []
  );

  useEffect(() => {
    const d = deuda?.nextDueDate?.toDate?.();

    if (!d) {
      setNextDueStr("");
      return;
    }

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");

    setNextDueStr(`${yyyy}-${mm}-${dd}`);
  }, [deuda?.nextDueDate]);

  const saveNextDue = useCallback(
    async (value = nextDueStr) => {
      try {
        const payload = value
          ? { nextDueDate: Timestamp.fromDate(new Date(`${value}T00:00:00`)) }
          : { nextDueDate: null };

        await updateDoc(doc(db, "deudas", deudaId), payload);

        setDeuda((prev) => ({ ...prev, ...payload }));
        setNextDueStr(value || "");

        notify.success("Próximo día de cobranza actualizado.");
      } catch (e) {
        console.error(e);
        notify.error("No se pudo actualizar la fecha de próximo cobro.");
      }
    },
    [deudaId, nextDueStr]
  );

  // Fecha de inicio verificada (autoritativa): cuando está activa, el motor V2
  // usa la fecha de inicio contractual como ancla del cálculo (mora/plan/panel)
  // en vez del pago más antiguo. El onSnapshot de la deuda refresca el perfil.
  const toggleFechaVerificada = useCallback(async () => {
    const nuevo = !deuda?.fechaInicioVerificada;
    try {
      await updateDoc(doc(db, "deudas", deudaId), { fechaInicioVerificada: nuevo });
      setDeuda((prev) => ({ ...prev, fechaInicioVerificada: nuevo }));
      registrarAuditoria({
        accion: "deudas.editar",
        entidadTipo: "deuda",
        entidadId: deudaId,
        antes: { fechaInicioVerificada: !nuevo },
        despues: { fechaInicioVerificada: nuevo },
      }).catch(() => {});
      notify.success(
        nuevo
          ? "Fecha de inicio marcada como verificada: ahora es el ancla del cálculo."
          : "Fecha de inicio ya no es autoritativa: vuelve al cálculo por defecto."
      );
    } catch (e) {
      console.error(e);
      notify.error("No se pudo actualizar el estado de la fecha verificada.");
    }
  }, [deuda?.fechaInicioVerificada, deudaId]);

  useEffect(() => {
    let isMounted = true;

    const deudaRef = doc(db, "deudas", deudaId);
    const clienteRef = doc(db, "clientes", clienteId);

    const unsubDeuda = onSnapshot(
      deudaRef,
      async (deudaSnap) => {
        try {
          if (!isMounted) return;

          if (deudaSnap.exists()) {
            const datosDeuda = deudaSnap.data();
            setDeuda(datosDeuda);
            setEscrituracionStatus(datosDeuda.escrituracionStatus || "no_escriturado");

            // Cargar comisiones vinculadas (fire-and-forget, no bloquea la vista)
            getComisionesPorDeuda(deudaId)
              .then((coms) => { if (isMounted) setComisionesDeuda(coms || []); })
              .catch((err) => console.warn("getComisionesPorDeuda:", err));

            if (datosDeuda?.responsableId) {
              try {
                // El responsable puede ser un USUARIO (cobrador) o un CLIENTE
                // (responsable "fuera del país", seleccionado del catálogo de
                // clientes). Igual que la lista de deudas, se intenta usuarios
                // y luego clientes como fallback; antes solo leía usuarios y por
                // eso mostraba "Sin responsable" cuando el responsable era cliente.
                let respSnap = await getDoc(doc(db, "usuarios", datosDeuda.responsableId));
                if (!respSnap.exists()) {
                  respSnap = await getDoc(doc(db, "clientes", datosDeuda.responsableId));
                }

                if (isMounted && respSnap.exists()) {
                  setResponsable(respSnap.data());
                } else if (isMounted) {
                  setResponsable(null);
                }
              } catch (err) {
                console.error("Error cargando responsable:", err);
                if (isMounted) setResponsable(null);
              }
            } else {
              setResponsable(null);
            }
          } else {
            setDeuda(null);
          }
        } catch (err) {
          console.error("Error leyendo deuda:", err);
        } finally {
          if (isMounted) setLoading(false);
        }
      },
      (err) => {
        console.error("Error suscribiendo deuda:", err);
        if (isMounted) setLoading(false);
      }
    );

    const cargarCliente = async () => {
      try {
        const clienteSnap = await getDoc(clienteRef);

        if (!isMounted) return;

        if (clienteSnap.exists()) {
          setCliente(clienteSnap.data());
        } else {
          setCliente(null);
        }
      } catch (err) {
        console.error("Error cargando cliente:", err);
      }
    };

    cargarCliente();

    return () => {
      isMounted = false;
      unsubDeuda();
    };
  }, [clienteId, deudaId]);

  const handleChangeEscrituracionStatus = useCallback(
    async (nuevoStatus) => {
      try {
        await updateDoc(doc(db, "deudas", deudaId), {
          escrituracionStatus: nuevoStatus,
        });

        setEscrituracionStatus(nuevoStatus);
        setDeuda((prev) => ({ ...prev, escrituracionStatus: nuevoStatus }));

        notify.success("Estatus de escrituración actualizado.");
      } catch (err) {
        console.error(err);
        notify.error("Error al actualizar estatus.");
      }
    },
    [deudaId]
  );

  const cargarArchivos = useCallback(async () => {
    if (!deudaId) return;

    const storage = getStorage();
    const folderRef = ref(storage, `deudas/${deudaId}/`);

    try {
      const res = await listAll(folderRef);

      if (!res.items.length) {
        setArchivos([]);
        return;
      }

      const items = await Promise.all(
        res.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          const nombre = itemRef.name;
          const ext = nombre.split(".").pop().toLowerCase();

          return { nombre, url, ref: itemRef, ext };
        })
      );

      setArchivos(items);
    } catch {
      setArchivos([]);
    }
  }, [deudaId]);

  useEffect(() => {
    cargarArchivos();
  }, [cargarArchivos, subiendo]);

  useEffect(() => {
    if (!deudaId) {
      setCajaMovimientosPorPago(new Map());
      return undefined;
    }

    const q = query(collection(db, "cajas_movimientos"), where("deudaId", "==", deudaId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = new Map();
        snap.forEach((item) => {
          const data = item.data();
          if (data.cancelado === true || data.activo === false) return;
          const pagoId = String(data.deudaPagoId || "");
          if (pagoId) next.set(pagoId, { id: item.id, ...data });
        });
        setCajaMovimientosPorPago(next);
      },
      (err) => {
        console.error("Error leyendo movimientos de caja ligados:", err);
        setCajaMovimientosPorPago(new Map());
      }
    );

    return () => unsub();
  }, [deudaId]);

  useEffect(() => {
    if (!deudaId || !puedeVerPromesas) {
      setPromesasPago([]);
      return undefined;
    }

    const unsub = escucharPromesasPorDeuda(deudaId, setPromesasPago);
    return () => unsub?.();
  }, [deudaId, puedeVerPromesas]);

  useEffect(() => {
    if (!deudaId || !puedeVerGestiones) {
      setGestionesCobranza([]);
      return undefined;
    }

    const unsub = escucharGestionesPorDeuda(deudaId, setGestionesCobranza);
    return () => unsub?.();
  }, [deudaId, puedeVerGestiones]);

  const subirArchivo = useCallback(
    async (file) => {
      if (!file) return;

      setSubiendo(true);

      try {
        const storage = getStorage();
        const fileRef = ref(storage, `deudas/${deudaId}/${Date.now()}_${file.name}`);
        await uploadBytes(fileRef, file);
      } catch (err) {
        console.error("Error subiendo archivo:", err);
      } finally {
        setSubiendo(false);
      }
    },
    [deudaId]
  );

  const handleInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) subirArchivo(file);
    },
    [subirArchivo]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setArrastrando(false);

      const file = e.dataTransfer.files?.[0];
      if (file) subirArchivo(file);
    },
    [subirArchivo]
  );

  const eliminar = useCallback(async (fileRef) => {
    if (!await dialogService.confirm("¿Eliminar este archivo?")) return;

    try {
      await deleteObject(fileRef);
      setSubiendo((x) => !x);
    } catch (err) {
      console.error("Error eliminando archivo:", err);
    }
  }, []);

  const toggleActivo = useCallback(async () => {
    if (!deuda) return;

    const nuevaActivo = !deuda.activo;

    if (!await dialogService.confirm(nuevaActivo ? "¿Reactivar esta deuda?" : "¿Desactivar esta deuda?")) return;

    try {
      await updateDoc(doc(db, "deudas", deudaId), { activo: nuevaActivo });
      await registrarAuditoria({
        accion: nuevaActivo ? "deudas.reactivar" : "deudas.desactivar",
        entidadTipo: "deuda",
        entidadId: deudaId,
        antes: { activo: deuda.activo },
        despues: { activo: nuevaActivo },
      });
      setDeuda((prev) => ({ ...prev, activo: nuevaActivo }));
      notify.success(`Deuda ${nuevaActivo ? "reactivada" : "desactivada"} correctamente`);
    } catch (err) {
      console.error(err);
      notify.error("Error al actualizar estado de la deuda");
    }
  }, [deuda, deudaId]);

  // Gestión telefónica (DNC): activa por default. Si se desactiva, los paneles
  // de call center (web y app de escritorio) marcan la cuenta "No llamar" y la
  // mandan al fondo de la cola. Campo persistente `gestionTelefonicaActiva` en
  // la deuda → la cola lo lee directo (sin depender del historial de gestiones).
  const toggleGestionTelefonica = useCallback(async () => {
    if (!deuda) return;
    // undefined se trata como activa (default).
    const activaActual = deuda.gestionTelefonicaActiva !== false;
    const nuevaActiva = !activaActual;
    if (
      !(await dialogService.confirm(
        nuevaActiva
          ? "¿Reactivar la gestión telefónica de esta cuenta? Volverá a entrar a la cola de llamadas."
          : "¿Desactivar la gestión telefónica? El call center NO llamará a esta cuenta (No llamar)."
      ))
    )
      return;

    try {
      await updateDoc(doc(db, "deudas", deudaId), {
        gestionTelefonicaActiva: nuevaActiva,
      });
      await registrarAuditoria({
        accion: nuevaActiva
          ? "deudas.gestionTelefonica.activar"
          : "deudas.gestionTelefonica.desactivar",
        entidadTipo: "deuda",
        entidadId: deudaId,
        antes: { gestionTelefonicaActiva: activaActual },
        despues: { gestionTelefonicaActiva: nuevaActiva },
      });
      setDeuda((prev) => ({ ...prev, gestionTelefonicaActiva: nuevaActiva }));
      notify.success(
        nuevaActiva
          ? "Gestión telefónica reactivada"
          : "Gestión telefónica desactivada (No llamar)"
      );
    } catch (err) {
      console.error(err);
      notify.error("Error al actualizar la gestión telefónica");
    }
  }, [deuda, deudaId]);

  // Sprint ENG-FIX (backwards-compat): marcar engancheCobradoFuera a true|false
  // explícitamente. Recalcula saldoRestante con el nuevo valor para que el
  // motor de cobranza vea la realidad.
  //   - nuevoValor=true  → cobrado por fuera (suma)
  //   - nuevoValor=false → pendiente (no suma)
  const setEngancheCobradoFueraFlag = useCallback(async (nuevoValor) => {
    if (!deuda) return;
    const mensaje = nuevoValor
      ? `¿Marcar enganche de ${money(deuda.enganche)} como cobrado por fuera? Se sumará al total pagado y reducirá el saldo.`
      : `¿Marcar enganche de ${money(deuda.enganche)} como pendiente / no cobrado? Se descontará del total pagado y el saldo aumentará.`;
    if (!await dialogService.confirm(mensaje)) return;

    try {
      const pagosArr = Array.isArray(deuda.pagos) ? deuda.pagos : [];
      let positivos = 0;
      let engEnArr = false;
      for (const p of pagosArr) {
        const c = n(p?.cantidad);
        if (c > 0) positivos += c;
        if (String(p?.tipo || "").toLowerCase() === "enganche" && c > 0) engEnArr = true;
      }
      const monto = n(deuda.monto ?? deuda.importe);
      const totalConFlag = positivos + (!engEnArr && nuevoValor ? n(deuda.enganche) : 0);
      const nuevoSaldo = Math.max(Math.round((monto - totalConFlag) * 100) / 100, 0);

      const patch = {
        engancheCobradoFuera: nuevoValor,
        saldoRestante: nuevoSaldo,
      };
      await updateDoc(doc(db, "deudas", deudaId), patch);
      await registrarAuditoria({
        accion: "deudas.setEngancheCobradoFuera",
        entidadTipo: "deuda",
        entidadId: deudaId,
        antes: { engancheCobradoFuera: deuda.engancheCobradoFuera, saldoRestante: deuda.saldoRestante },
        despues: patch,
      });
      setDeuda((prev) => ({ ...prev, ...patch }));
      notify.success(nuevoValor ? "Enganche marcado como cobrado por fuera" : "Enganche marcado como pendiente");
    } catch (err) {
      console.error(err);
      notify.error("Error al actualizar la deuda: " + (err?.message || err));
    }
  }, [deuda, deudaId]);

  const handleEditar = useCallback(
    () => navigate(`/app/cliente/${clienteId}/deudas/${deudaId}/editar`),
    [navigate, clienteId, deudaId]
  );

  const handleCrearPromesa = useCallback(
    async (e) => {
      e.preventDefault();
      if (!puedeCrearPromesas || savingOperativo) return;
      if (!promesaForm.montoPrometido || !promesaForm.fechaPromesa) {
        notify.error("Captura monto y fecha de promesa.");
        return;
      }

      try {
        setSavingOperativo(true);
        if (promesaEditandoId) {
          await actualizarPromesaPago(promesaEditandoId, {
            montoPrometido: promesaForm.montoPrometido,
            fechaPromesa: promesaForm.fechaPromesa,
            notas: promesaForm.notas,
          });
        } else {
          await crearPromesaPago({
            clienteId,
            deudaId,
            montoPrometido: promesaForm.montoPrometido,
            fechaPromesa: promesaForm.fechaPromesa,
            notas: promesaForm.notas,
          });
        }
        setPromesaEditandoId(null);
        setPromesaForm({ montoPrometido: "", fechaPromesa: "", notas: "" });
      } catch (err) {
        console.error(err);
        notify.error("No se pudo registrar la promesa.");
      } finally {
        setSavingOperativo(false);
      }
    },
    [clienteId, deudaId, promesaEditandoId, promesaForm, puedeCrearPromesas, savingOperativo]
  );

  const editarPromesa = useCallback((promesa) => {
    if (!puedeEditarPromesas) return;

    setPromesaEditandoId(promesa.id);
    setPromesaForm({
      montoPrometido: promesa.montoPrometido || "",
      fechaPromesa: promesa.fechaPromesa || "",
      notas: promesa.notas || "",
    });
  }, [puedeEditarPromesas]);

  const cancelarEdicionPromesa = useCallback(() => {
    setPromesaEditandoId(null);
    setPromesaForm({ montoPrometido: "", fechaPromesa: "", notas: "" });
  }, []);

  const cambiarEstadoPromesa = useCallback(
    async (promesa, estado) => {
      if (!puedeEditarPromesas || savingOperativo) return;

      try {
        setSavingOperativo(true);
        await actualizarPromesaPago(promesa.id, { estado });
      } catch (err) {
        console.error(err);
        notify.error("No se pudo actualizar la promesa.");
      } finally {
        setSavingOperativo(false);
      }
    },
    [puedeEditarPromesas, savingOperativo]
  );

  const handleCrearGestion = useCallback(
    async (e) => {
      e.preventDefault();
      if (!puedeCrearGestiones || savingOperativo) return;
      if (!gestionForm.resultado.trim()) {
        notify.error("Captura el resultado de la gestion.");
        return;
      }

      try {
        setSavingOperativo(true);
        await crearGestionCobranza({
          clienteId,
          deudaId,
          tipo: gestionForm.tipo,
          resultado: gestionForm.resultado,
          comentario: gestionForm.comentario,
        });
        setGestionForm({ tipo: "llamada", resultado: "", comentario: "" });
      } catch (err) {
        console.error(err);
        notify.error("No se pudo registrar la gestion.");
      } finally {
        setSavingOperativo(false);
      }
    },
    [clienteId, deudaId, gestionForm, puedeCrearGestiones, savingOperativo]
  );

  const abrirModalPago = useCallback((i) => {
    setModalPago({ abierto: true, index: i });
  }, []);

  const cerrarModalPago = useCallback(() => {
    setModalPago({ abierto: false, index: 0 });
  }, []);

  const buscarMovimientosCajaDelPago = useCallback(async (pago) => {
    const refs = new Map();
    const cajaId = pago?.cajaMovimientoId || pago?.cajaId || pago?.movimientoCajaId;

    if (cajaId) {
      const ref = doc(db, "cajas_movimientos", cajaId);
      const snap = await getDoc(ref);
      if (snap.exists()) refs.set(snap.id, { ref, data: snap.data() });
    }

    const pagoId = String(pago?.pagoId || "");
    if (pagoId) {
      const snap = await getDocs(
        query(
          collection(db, "cajas_movimientos"),
          where("deudaId", "==", deudaId),
          where("deudaPagoId", "==", pagoId)
        )
      );
      snap.forEach((item) => refs.set(item.id, { ref: item.ref, data: item.data() }));
    }

    return Array.from(refs.values());
  }, [deudaId]);

  const guardarPagoEditado = useCallback(
    async (pagoEditado) => {
      const pagoOriginal = deuda?.pagos?.[modalPago.index] || {};
      const fechaTs = toTimestampFromDateOnly(pagoEditado?.fecha);
      const pagoActualizado = {
        ...pagoOriginal,
        ...pagoEditado,
        pagoId: pagoOriginal?.pagoId || pagoEditado?.pagoId || getPagoId(pagoOriginal, modalPago.index),
        cantidad: n(pagoEditado?.cantidad),
        fecha: fechaTs,
        ...metodoPagoPayload(pagoEditado?.metodo || pagoOriginal?.metodo),
      };

      const movimientosCaja = await buscarMovimientosCajaDelPago(pagoOriginal);
      if (movimientosCaja.length) {
        const ok = await dialogService.confirm(
          `Este pago tiene ${movimientosCaja.length} movimiento(s) de caja ligado(s).\n\n` +
            "Al guardar, se sincronizaran fecha, monto, metodo, referencia y notas en caja para que CxC y caja sigan cuadrando.\n\n" +
            "¿Continuar?"
        );
        if (!ok) return;
      }

      const pagosActualizados = (deuda?.pagos || []).map((p, i) =>
        i === modalPago.index ? pagoActualizado : p
      );

      const fechasValidas = pagosActualizados
        .map((p) => toJsDate(p?.fecha))
        .filter(Boolean)
        .sort((a, b) => b - a);

      const saldo = calcularSaldoDeuda(deuda, pagosActualizados);
      const esDeudaEnDevolucion = deuda?.devolucion === true || deuda?.estado === "en_devolucion" || deuda?.estado === "devuelta";
      const updatePayload = {
        pagos: pagosActualizados,
        lastPaymentDate: fechasValidas.length ? Timestamp.fromDate(fechasValidas[0]) : null,
        saldo,
        ...(!esDeudaEnDevolucion ? { estado: saldo > 0 ? "activa" : "liquidada", activo: saldo > 0 } : {}),
      };

      const batch = writeBatch(db);
      batch.update(doc(db, "deudas", deudaId), updatePayload);

      const cajaPayload = {
        monto: Math.abs(n(pagoActualizado.cantidad)),
        tipo: n(pagoActualizado.cantidad) < 0 || String(pagoActualizado?.tipo || "").toLowerCase().includes("devolucion") ? "salida" : "entrada",
        fecha: toDateOnly(pagoActualizado.fecha),
        fechaMs: getPagoDateMs(pagoActualizado.fecha),
        ...metodoPagoPayload(pagoActualizado.metodo || ""),
        referencia: pagoActualizado.referencia || "",
        notas: pagoActualizado.notas || "",
        deudaId,
        deudaPagoId: pagoActualizado.pagoId,
        updatedAt: Timestamp.now(),
        origen: "deudas.pago",
      };

      movimientosCaja.forEach(({ ref }) => {
        batch.update(ref, cajaPayload);
      });

      await batch.commit();
      await registrarAuditoria({
        accion: "deudas.editarPago",
        entidadTipo: "deuda",
        entidadId: deudaId,
        antes: { pago: pagoOriginal, index: modalPago.index },
        despues: {
          pago: pagoActualizado,
          index: modalPago.index,
          cajaSincronizada: movimientosCaja.length,
        },
      });

      setDeuda((prev) => ({
        ...prev,
        ...updatePayload,
      }));

      cerrarModalPago();
    },
    [deuda, modalPago.index, deudaId, cerrarModalPago, buscarMovimientosCajaDelPago]
  );

  const eliminarPago = useCallback(async () => {
    const pagoOriginal = deuda?.pagos?.[modalPago.index] || {};
    const movimientosCaja = await buscarMovimientosCajaDelPago(pagoOriginal);
    if (movimientosCaja.length) {
      const ok = await dialogService.confirm(
        `Este pago tiene ${movimientosCaja.length} movimiento(s) de caja ligado(s).\n\n` +
          "Al eliminar el pago, esos movimientos se cancelaran logicamente en caja para evitar entradas huerfanas.\n\n" +
          "¿Continuar?"
      );
      if (!ok) return;
    }

    const pagosActualizados = (deuda?.pagos || []).filter((_, i) => i !== modalPago.index);

    const fechasValidas = pagosActualizados
      .map((p) => toJsDate(p?.fecha))
      .filter(Boolean)
      .sort((a, b) => b - a);

    const saldo = calcularSaldoDeuda(deuda, pagosActualizados);
    const esDeudaEnDevolucion = deuda?.devolucion === true || deuda?.estado === "en_devolucion" || deuda?.estado === "devuelta";
    const updatePayload = {
      pagos: pagosActualizados,
      lastPaymentDate: fechasValidas.length ? Timestamp.fromDate(fechasValidas[0]) : null,
      saldo,
      ...(!esDeudaEnDevolucion ? { estado: saldo > 0 ? "activa" : "liquidada", activo: saldo > 0 } : {}),
    };

    const batch = writeBatch(db);
    batch.update(doc(db, "deudas", deudaId), updatePayload);
    movimientosCaja.forEach(({ ref }) => {
      batch.update(ref, {
        cancelado: true,
        activo: false,
        canceladoEn: Timestamp.now(),
        motivoCancelacion: "Pago CxC eliminado",
        updatedAt: Timestamp.now(),
      });
    });
    await batch.commit();
    await registrarAuditoria({
      accion: "deudas.eliminarPago",
      entidadTipo: "deuda",
      entidadId: deudaId,
      antes: { pago: pagoOriginal, index: modalPago.index },
      despues: { cajaCancelada: movimientosCaja.length },
    });

    setDeuda((prev) => ({
      ...prev,
      ...updatePayload,
    }));

    cerrarModalPago();
  }, [deuda, modalPago.index, deudaId, cerrarModalPago, buscarMovimientosCajaDelPago]);

  const abrirDevolucion = useCallback(() => {
    setGastosAdmin(0);
    setComisionVenta(0);
    setMotivoDevolucion("");
    setOpenDev(true);
  }, []);

  const confirmarActivarDevolucion = useCallback(async () => {
    try {
      const finActual = computeFinanzas(deuda || {});
      // Redondeo a centavos para evitar floats residuales (ej. 4.65e-10) que
      // hacen que penalizacion = abonado NO cierre como "devuelta" sino que
      // quede colgada en "en_devolucion" con saldo basura. Caso real: deuda
      // DE-00003 Antonio Garrido (jun-2026) quedó con saldo 4.65e-10 luego
      // de penalizacion 100% por residuo de resta de floats.
      const abonadoTotalRound = r2(finActual.abonadoTotal);
      const gastosAdministrativos = r2(n(gastosAdmin));
      const comisionVentaMonto = r2(n(comisionVenta));
      const multa = r2(gastosAdministrativos + comisionVentaMonto);
      const paqueteDevolucionId = `DEV-${deudaId}-${Date.now()}`;
      const saldoDevolucion = r2(Math.max(abonadoTotalRound - multa, 0));
      const motivo = String(motivoDevolucion || "").trim() || "Devolucion solicitada";
      const ok = await dialogService.confirm(
        "Se iniciará un paquete de devolución. Las propiedades se liberarán y la deuda cambiará de estado. Esta acción no se puede deshacer.",
        {
          titulo: "Iniciar devolución",
          peligro: true,
          confirmText: "Sí, iniciar devolución",
          detalles: [
            { label: "Cliente", value: [cliente?.nombre, cliente?.apellidos].filter(Boolean).join(" ").trim() || clienteId },
            { label: "Abonado actual", value: money(finActual.abonadoTotal) },
            { label: "Penalización total", value: money(multa) },
            { label: "Saldo a devolver", value: money(saldoDevolucion) },
          ],
        }
      );

      if (!ok) return;

      const saldoAntesRound = r2(finActual.saldoRestante ?? finActual.saldoVenta ?? 0);
      const devolucionInfo = {
        paqueteDevolucionId,
        estadoPaquete: saldoDevolucion > 0 ? "abierto" : "cerrado_sin_saldo",
        motivo,
        gastosAdministrativos,
        comisionVenta: comisionVentaMonto,
        penalizacionTotal: multa,
        abonadoTotal: abonadoTotalRound,
        baseDevolucion: saldoDevolucion,
        saldoAntes: saldoAntesRound,
        saldoDespues: saldoDevolucion,
        validacionSaldo: {
          abonadoTotal: abonadoTotalRound,
          penalizacionTotal: multa,
          baseDevolucion: saldoDevolucion,
        },
        iniciadaEn: Timestamp.now(),
      };

      const ajusteNegativo =
        multa > 0
          ? {
              pagoId: `AJ-${paqueteDevolucionId}`,
              cantidad: -Math.abs(multa),
        metodo: "AJUSTE DEVOLUCION",
              tipo: "ajuste_devolucion",
              fecha: Timestamp.now(),
              notas: motivo,
              paqueteDevolucionId,
        nota: "Penalización por devolución (gastos + comisión)",
            }
          : null;

      const pagosConAjuste = ajusteNegativo ? [...(deuda?.pagos || []), ajusteNegativo] : [...(deuda?.pagos || [])];

      if (saldoDevolucion <= 0) {
        const payload = {
          pagos: pagosConAjuste,
          activo: false,
          devolucion: false,
          esDevolucion: false,
          estado: "devuelta",
          saldoRestante: 0,
          nextDueDate: null,
          comisionCancelacion: multa,
          devolucionInfo,
          paqueteDevolucionId,
        };

        await updateDoc(doc(db, "deudas", deudaId), payload);
        await registrarAuditoria({
          accion: "deudas.iniciarDevolucion",
          entidadTipo: "deuda",
          entidadId: deudaId,
          antes: deuda,
          despues: payload,
          detalle: "Devolucion cerrada sin saldo por devolver",
        });

        // Audit A-3: await + notify para que el operador vea si fallo la
        // liberacion. Si no, las propiedades quedan "vendidas" fantasma.
        try {
          await liberarPropiedadesDeDeuda({ ...deuda, pagos: pagosConAjuste });
        } catch (e) {
          console.error("No se pudieron liberar las propiedades asociadas:", e);
          notify.error(
            `Devolución registrada, pero la liberación de propiedades falló: ${e?.message || "error"}. Las propiedades pueden seguir marcadas como vendidas — libera manualmente.`
          );
        }

        setDeuda((prev) => ({
          ...prev,
          ...payload,
        }));

        // Audit A-4: await + mostrar errores. Si la cancelacion de CxPs de
        // comision falla, podemos terminar pagando dos veces al comisionista.
        try {
          const { canceladas, errores } = await cancelarComisionesEnCascada(deudaId, motivo);
          if (canceladas > 0) {
            notify.success(`${canceladas} CxP${canceladas > 1 ? "s" : ""} de comisión cancelada${canceladas > 1 ? "s" : ""} automáticamente.`);
          }
          if (errores.length > 0) {
            console.error("cancelarComisionesEnCascada errores:", errores);
            notify.error(
              `Devolución OK, pero la cancelación de comisiones reportó errores: ${errores.slice(0, 2).join(" | ")}. Revisa manualmente para evitar pagos duplicados.`
            );
          }
        } catch (e) {
          console.error("cancelarComisionesEnCascada fatal:", e);
          notify.error(
            `Devolución OK, pero NO se pudo cancelar la comisión: ${e?.message || "error"}. Cancela manualmente para evitar pago duplicado.`
          );
        }

        setOpenDev(false);
        notify.success("No hay saldo por devolver. Se registró el ajuste, la devolución quedó cerrada y las propiedades fueron liberadas.");
        return;
      }

      const base = new Date();
      base.setHours(0, 0, 0, 0);

      // Audit A-9: en devolucion no usamos nextDueDate como "siguiente pago".
      // No hay agenda real con el cliente — el saldo es POR DEVOLVER (a el),
      // no por COBRAR. Lo dejamos null para que reportes/agendas no lo listen.
      const payload = {
        pagos: pagosConAjuste,
        devolucion: true,
        esDevolucion: true,
        estado: "en_devolucion",
        concepto: `Devolución ${deuda?.concepto || ""}`.trim(),
        saldoRestante: saldoDevolucion,
        lastPaymentDate: Timestamp.fromDate(base),
        nextDueDate: null,
        propiedadId: null,
        activo: true,
        comisionCancelacion: multa,
        devolucionInfo,
        paqueteDevolucionId,
      };

      await updateDoc(doc(db, "deudas", deudaId), payload);
      await registrarAuditoria({
        accion: "deudas.iniciarDevolucion",
        entidadTipo: "deuda",
        entidadId: deudaId,
        antes: deuda,
        despues: payload,
      });

      // Audit A-3: await + notify de fallo (mismo patron que arriba)
      try {
        await liberarPropiedadesDeDeuda({ ...deuda, pagos: pagosConAjuste });
      } catch (e) {
        console.error("No se pudieron liberar las propiedades asociadas:", e);
        notify.error(
          `Devolución registrada, pero la liberación de propiedades falló: ${e?.message || "error"}. Las propiedades pueden seguir marcadas como vendidas — libera manualmente.`
        );
      }

      setDeuda((prev) => ({
        ...prev,
        ...payload,
      }));

      // Audit A-4: await + mostrar errores
      try {
        const { canceladas, errores } = await cancelarComisionesEnCascada(deudaId, motivo);
        if (canceladas > 0) {
          notify.success(`${canceladas} CxP${canceladas > 1 ? "s" : ""} de comisión cancelada${canceladas > 1 ? "s" : ""} automáticamente.`);
        }
        if (errores.length > 0) {
          console.error("cancelarComisionesEnCascada errores:", errores);
          notify.error(
            `Devolución OK, pero la cancelación de comisiones reportó errores: ${errores.slice(0, 2).join(" | ")}. Revisa manualmente para evitar pagos duplicados.`
          );
        }
      } catch (e) {
        console.error("cancelarComisionesEnCascada fatal:", e);
        notify.error(
          `Devolución OK, pero NO se pudo cancelar la comisión: ${e?.message || "error"}. Cancela manualmente para evitar pago duplicado.`
        );
      }

      setOpenDev(false);
      notify.success("Devolución activada, ajuste registrado y propiedades liberadas.");
    } catch (e) {
      console.error(e);
      notify.error("Error al activar devolución");
    }
  }, [deuda, deudaId, gastosAdmin, comisionVenta, motivoDevolucion]);

  const fin = useMemo(() => computeFinanzas(deuda || {}), [deuda]);
  const mensualidadPactada = useMemo(() => {
  return n(deuda?.mensualidad ?? deuda?.pagoMensual ?? 0);
}, [deuda]);
  const esDevolucion = isDevolucionDeuda(deuda || {});
  const montoInicial = esDevolucion ? fin.baseDev : fin.importeVenta;
  const enganche = fin.enganche;
  const totalMostrado = esDevolucion ? fin.totalDevuelto : fin.totalPagadoVenta;
  // Pagos realizados SIN enganche = total abonado − enganche cobrado. Robusto
  // en los 3 casos (enganche en pagos, cobrado por fuera, o marcado pendiente).
  const pagosSinEnganche = Math.max(
    (fin.totalPagadoVenta || 0) - (fin.engancheCobrado || 0),
    0
  );
  const saldoRestante = esDevolucion ? fin.pendienteDev : fin.saldoVenta;
  const progreso = Math.min(100, Math.max(0, (totalMostrado / (montoInicial || 1)) * 100));
  const perfilCobranza = useMemo(
    () => calcularPerfilCobranzaV2(deuda || {}),
    [deuda]
  );

  const pagos = useMemo(() => {
    return Array.isArray(deuda?.pagos) ? deuda.pagos : [];
  }, [deuda]);

  // Constancia de liquidación: deriva la fecha del ÚLTIMO pago registrado
  // (la fecha máxima del arreglo deuda.pagos). Cae a "hoy" si no hay pagos.
  const fechaUltimoPagoISO = useMemo(() => {
    const ms = pagos
      .map((p) => toJsDate(p?.fecha)?.getTime())
      .filter((t) => typeof t === "number" && !Number.isNaN(t));
    if (!ms.length) return hoyLocalISO();
    const max = new Date(Math.max(...ms));
    return toLocalISODate(max);
  }, [pagos]);

  const abrirConstancia = useCallback(async () => {
    // La constancia se puede emitir aunque la deuda NO esté liquidada (algunos
    // clientes la piden antes de liquidar o en su último pago). El documento es
    // el mismo; si aún hay saldo, confirmamos para que sea una acción deliberada.
    if (saldoRestante > 0) {
      const ok = await dialogService.confirm(
        `Esta cuenta aún tiene un saldo de ${money(saldoRestante)}. ` +
          `La constancia declara la liquidación total. ¿Generarla de todas formas?`
      );
      if (!ok) return;
    }
    setConstanciaFecha(fechaUltimoPagoISO);
    setConstanciaTrato("C.");
    setConstanciaOpen(true);
  }, [fechaUltimoPagoISO, saldoRestante]);

  const generarConstancia = useCallback(() => {
    // El value del input type="date" es "YYYY-MM-DD". Construimos el Date a
    // partir de sus componentes (mediodía local) para evitar el corrimiento
    // de zona horaria que produce new Date("YYYY-MM-DD") (interpretado UTC).
    let fechaDate = new Date();
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(constanciaFecha || "");
    if (m) {
      const [, y, mo, d] = m;
      fechaDate = new Date(Number(y), Number(mo) - 1, Number(d), 12, 0, 0);
    }
    generarConstanciaLiquidacionPDF({
      deuda,
      cliente,
      clienteId,
      deudaId,
      fecha: fechaDate,
      tratamiento: constanciaTrato,
    });
    setConstanciaOpen(false);
  }, [constanciaFecha, constanciaTrato, deuda, cliente, clienteId, deudaId]);

  const pagosSinCaja = useMemo(
    () =>
      pagos.filter((p, index) => {
        const cantidad = n(p?.cantidad);
        const tipo = String(p?.tipo || "").toLowerCase();
        if (cantidad === 0) return false;
        if (tipo === "ajuste_devolucion" || tipo === "ajuste") return false;
        const pagoId = getPagoId(p, index);
        return !(getPagoCajaId(p) || cajaMovimientosPorPago.has(pagoId));
      }).length,
    [pagos, cajaMovimientosPorPago]
  );

  const propsListUI = useMemo(() => {
    if (!deuda) return [];

    if (Array.isArray(deuda.propiedades) && deuda.propiedades.length) {
      return deuda.propiedades;
    }

    if (deuda.propiedadNombre || deuda.propiedadFolio || deuda.manzana || deuda.lote) {
      return [
        {
          nombre: deuda.propiedadNombre,
          folio: deuda.propiedadFolio,
          codigo: deuda.propiedadCodigo,
          manzana: deuda.manzana,
          lote: deuda.lote,
          fraccionamientoNombre: deuda.fraccionamiento,
        },
      ];
    }

    return [];
  }, [deuda]);

  const propsLabelUI = useMemo(() => {
    if (!propsListUI.length) return null;

    return propsListUI
      .map((p) => {
        const cod = p.codigo || (p.manzana || p.lote ? `MZ${p.manzana || "?"}-LT${p.lote || "?"}` : "");
        const folio = p.folio || "";
        const nombre = p.nombre || "";

        return [cod, folio, nombre].filter(Boolean).join(" ");
      })
.join(" · ");
  }, [propsListUI]);
void propsLabelUI;

const clienteNombreCompleto = useMemo(() => {
  return [cliente?.nombre, cliente?.apellidos].filter(Boolean).join(" ").trim() || "Cliente sin nombre";
}, [cliente]);

const clienteIniciales = useMemo(() => {
  return clienteNombreCompleto
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}, [clienteNombreCompleto]);
const fotoCliente = cliente?.fotoPerfil || cliente?.foto || cliente?.imagenUrl || "";

const fechaInicioDeuda = useMemo(() => {
  const fecha =
    deuda?.fechaInicio ||
    deuda?.fechaInicioDeuda ||
    deuda?.fechaContrato ||
    deuda?.fechaVenta ||
    deuda?.creada;

  return formatDateOnlyMx(fecha);
}, [deuda]);

const diasMora = useMemo(() => {
  const due = deuda?.nextDueDate?.toDate?.();
  if (!due) return 0;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  const diff = Math.floor((hoy - due) / 86400000);
  return diff > 0 ? diff : 0;
}, [deuda?.nextDueDate]);

const estadoDeudaUI = useMemo(() => {
  if (!deuda?.activo) {
    return {
      label: "Inactiva",
      className: "estado-inactiva",
      detalle: "La deuda está desactivada.",
    };
  }

  if (esDevolucion && saldoRestante > 0) {
    return {
      label: "Devolución activa",
      className: "estado-devolucion",
      detalle: "Cuenta en proceso de devolución.",
    };
  }

  if (esDevolucion && saldoRestante <= 0) {
    return {
      label: "Devuelta",
      className: "estado-liquidada",
      detalle: "La devolución fue cerrada.",
    };
  }

  if (saldoRestante <= 0) {
    return {
      label: "Liquidada",
      className: "estado-liquidada",
      detalle: "La cuenta no presenta saldo pendiente.",
    };
  }

  if (diasMora > 0) {
    return {
      label: "Vencida",
      className: "estado-vencida",
      detalle: `${diasMora} días de atraso desde la próxima fecha de pago.`,
    };
  }

  if (Number(perfilCobranza?.saldoAtrasado || 0) > 0) {
    return {
      label: "Vencida",
      className: "estado-vencida",
      detalle: `${perfilCobranza.mesesAtraso || 0} mensualidades pendientes por calendario financiero.`,
    };
  }

  return {
    label: "Al corriente",
    className: "estado-corriente",
    detalle: "La deuda no presenta vencimiento activo.",
  };
}, [deuda?.activo, esDevolucion, saldoRestante, diasMora, perfilCobranza]);

const clienteInfo = useMemo(() => {
  return [
    ["Teléfono", cliente?.telefono || "-"],
    ["Email", cliente?.email || "-"],
    ["Dirección", cliente?.direccion || "-"],
    ["Fecha de inicio", fechaInicioDeuda],
    ["Cliente ID", clienteId],
  ];
}, [cliente, clienteId, fechaInicioDeuda]);

  if (loading) return <div className="detalle-deuda">Cargando deuda...</div>;
  if (!deuda || !cliente) return <div className="detalle-deuda">Datos no encontrados.</div>;
  if (!puedeVerDeudas) {
    return (
      <div className="detalle-deuda">
        <div className="detalle-panel">
          <h3>Acceso restringido</h3>
          <p>No tienes permiso para ver el detalle de esta deuda.</p>
          <button onClick={() => navigate(`/app/cliente/${clienteId}`)}>
            Volver al cliente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="detalle-deuda">
      <div className="deuda-hero">
  <div className="cliente-hero-card">
    <div className="cliente-avatar">
  {fotoCliente ? (
    <img src={fotoCliente} alt={clienteNombreCompleto} />
  ) : (
    clienteIniciales
  )}
</div>

    <div className="cliente-hero-info">
      <p className="hero-label">Cliente</p>
      <h2>{clienteNombreCompleto}</h2>

      <div className="cliente-quick-info">
        <span>{cliente?.telefono || "Sin teléfono"}</span>
        <span>{cliente?.email || "Sin email"}</span>
        <span>{cliente?.direccion || "Sin dirección"}</span>
      </div>
    </div>
  </div>

  <div className={`estado-deuda-card ${estadoDeudaUI.className}`}>
    <p className="hero-label">Estado de la deuda</p>
    <h3>{estadoDeudaUI.label}</h3>
    <span>{estadoDeudaUI.detalle}</span>
    {deuda?.activo && Number(deuda?.saldo || deuda?.saldoRestante || 0) > 0 && (
      <button
        type="button"
        onClick={() => setModalRenegAbierto(true)}
        style={{
          marginTop: 10, padding: "8px 14px", borderRadius: 8,
          background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
          color: "#fff", border: "none", fontWeight: 600,
          fontSize: 12, cursor: "pointer",
        }}
        title="Generar propuesta de renegociación con IA"
      >
        💬 Sugerir renegociación
      </button>
    )}
  </div>
</div>

<div className="detalle-grid-superior">
  <div className="detalle-panel">
    <h3>Datos del cliente</h3>

    <div className="detalle-info-grid">
      {clienteInfo.map(([label, value]) => (
        <div key={label} className="detalle-info-item">
          <span>{label}</span>
          <strong>{value}</strong>
          {label === "Fecha de inicio" && puedeEditarDeuda && (
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
                marginTop: 8,
                fontSize: 11,
                lineHeight: 1.3,
                fontWeight: 400,
                cursor: "pointer",
              }}
              title="Usa la fecha de inicio como ancla real del cálculo (mora/plan/panel)"
            >
              <input
                type="checkbox"
                checked={!!deuda?.fechaInicioVerificada}
                onChange={toggleFechaVerificada}
                style={{ marginTop: 2 }}
              />
              <span>
                <strong>Fecha verificada</strong> — usarla como ancla real del
                cálculo (mora / plan / panel) en vez del pago más antiguo.
              </span>
            </label>
          )}
          {label === "Fecha de inicio" &&
            !puedeEditarDeuda &&
            deuda?.fechaInicioVerificada && (
              <span style={{ fontSize: 11, color: "#16a34a", marginTop: 4 }}>
                ✓ Fecha verificada
              </span>
            )}
        </div>
      ))}
    </div>
  </div>

  <div className="detalle-panel">
    <h3>Datos de la deuda</h3>

    <div className="detalle-info-grid">
      <div className="detalle-info-item">
        <span>Concepto</span>
        <strong>{deuda.concepto || "-"}</strong>
      </div>

      <div className="detalle-info-item">
        <span>Referencia</span>
        <strong>{deuda.referencia || "-"}</strong>
      </div>

      <div className="detalle-info-item">
  <span>Propiedades</span>

  <div className="prop-summary">
    <strong>
      {deuda.propiedades?.length || 0} propiedades asignadas
    </strong>

    <span>
      {(deuda.propiedades || [])
        .slice(0, 3)
        .map(p => p.codigo || `MZ${p.manzana}-LT${p.lote}`)
        .join(", ")}
    </span>

    <button
      className="prop-toggle"
      onClick={() => setShowProps((v) => !v)}
    >
      {showProps ? "Ocultar" : "Ver propiedades"}
    </button>
  </div>

  {showProps && (
    <div className="prop-list">
      {(deuda.propiedades || []).map((p, i) => (
        <div className="prop-row" key={i}>
          <strong>{p.codigo || `MZ${p.manzana}-LT${p.lote}`}</strong>
          <span>{p.folio || "Sin folio"}</span>
          <small>{p.nombre || ""}</small>
        </div>
      ))}
    </div>
  )}
</div>

      <div className="detalle-info-item">
        <span>Responsable</span>
        <strong>
          {responsable
            ? `${responsable.nombre || ""} ${responsable.apellidos || ""}`.trim()
            : "Sin responsable"}
        </strong>
      </div>

      {comisionesDeuda.length > 0 && (
        <div className="detalle-info-item">
          <span>{comisionesDeuda.length === 1 ? "Comisionista" : "Comisionistas"}</span>
          <div className="comisionistas-deuda">
            {comisionesDeuda.map((c) => (
              <button
                key={c.id}
                type="button"
                className="comisionista-chip"
                onClick={() => navigate(`/app/comisiones/${c.id}`, { state: { comisionistaId: c.comisionistaId } })}
                title={`Ver detalle de la comisión — ${c.comisionistaNombre || "comisionista"}`}
              >
                <strong>{c.comisionistaNombre || "Sin nombre"}</strong>
                <small>
                  {c.tipoCalculo === "monto_fijo"
                    ? "Monto fijo"
                    : `${((c.porcentaje || 0) * 100).toFixed(1)}%`}
                  {" · "}
                  {c.estado || "—"}
                </small>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
</div>


      {esDevolucion && saldoRestante > 0 && (
        <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: "#fff3e0", border: "1px solid #ffcc80" }}>
          Esta deuda está en <strong>devolución</strong>. Los pagos futuros se registrarán como salidas negativas.
          <br />
          Base: <strong>{money(fin.baseDev)}</strong> · Devuelto: <strong>{money(fin.totalDevuelto)}</strong> · Pendiente:{" "}
          <strong>{money(fin.pendienteDev)}</strong> · Penalización: <strong>{money(fin.comision)}</strong>
        </Box>
      )}

      {pagosSinCaja > 0 && (
        <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: "#fff8e1", border: "1px solid #ffe082" }}>
          Hay <strong>{pagosSinCaja}</strong> pago(s) sin movimiento de caja ligado. Pueden ser históricos o requerir conciliación.
        </Box>
      )}

      <Dialog open={openDev} onClose={() => setOpenDev(false)} fullWidth maxWidth="xs">
        <DialogTitle>Iniciar devolución</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField
              label="Gastos administrativos"
              type="number"
              value={gastosAdmin}
              onChange={(e) => setGastosAdmin(Number(e.target.value))}
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              label="Comisión de venta"
              type="number"
              value={comisionVenta}
              onChange={(e) => setComisionVenta(Number(e.target.value))}
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              label="Motivo / acuerdo"
              value={motivoDevolucion}
              onChange={(e) => setMotivoDevolucion(e.target.value)}
              multiline
              minRows={2}
              placeholder="Ej. Devolucion autorizada con penalizacion parcial"
            />
            <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <Typography variant="body2">
                Abonado actual: <strong>{money(fin.abonadoTotal)}</strong>
              </Typography>
              <Typography variant="body2">
                Penalizacion: <strong>{money(n(gastosAdmin) + n(comisionVenta))}</strong>
              </Typography>
              <Typography variant="body2">
                Saldo estimado a devolver:{" "}
                <strong>{money(Math.max(n(fin.abonadoTotal) - n(gastosAdmin) - n(comisionVenta), 0))}</strong>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDev(false)}>Cancelar</Button>
          <Button onClick={confirmarActivarDevolucion} variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

<div className="stats stats-redisenadas">
  <div className="stat-box stat-primary">
    <p className="stat-label">{esDevolucion ? "Base devolución" : "Monto Inicial"}</p>
    <p className="stat-value">{money(montoInicial)}</p>
  </div>

  {!esDevolucion && (
    <div className="stat-box">
      <p className="stat-label">Enganche</p>
      <p className="stat-value">{money(enganche)}</p>
      {enganche > 0 && (
        <small style={{ display: "block", marginTop: 4 }}>
          {fin.engEnPagos ? (
            <span style={{ color: "#16a34a" }}>✓ registrado en pagos</span>
          ) : fin.engancheCobradoFuera ? (
            <span style={{ color: "#16a34a" }}>✓ cobrado por fuera</span>
          ) : fin.engancheMarcadoPendiente ? (
            <span style={{ color: "#dc2626" }}>⚠ pendiente / no cobrado</span>
          ) : (
            <span style={{ color: "#a16207" }} title="Deuda previa al cambio: el enganche se suma al total como antes. Marca explícitamente para migrar.">
              ◐ legacy (suma como antes)
            </span>
          )}
          {can("deudas.editar") && !fin.engEnPagos && (
            <span style={{ display: "block", marginTop: 4 }}>
              {!fin.engancheCobradoFuera && (
                <button
                  type="button"
                  onClick={() => setEngancheCobradoFueraFlag(true)}
                  style={{
                    marginRight: 4,
                    padding: "2px 6px",
                    fontSize: 11,
                    background: "#fff",
                    border: "1px solid #cbd5e1",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Marcar cobrado por fuera
                </button>
              )}
              {!fin.engancheMarcadoPendiente && (
                <button
                  type="button"
                  onClick={() => setEngancheCobradoFueraFlag(false)}
                  style={{
                    padding: "2px 6px",
                    fontSize: 11,
                    background: "#fff",
                    border: "1px solid #cbd5e1",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Marcar pendiente
                </button>
              )}
            </span>
          )}
        </small>
      )}
    </div>
  )}
  <div className="stat-box">
  <p className="stat-label">Mensualidad</p>
  <p className="stat-value">{money(mensualidadPactada)}</p>
</div>
  <div className="stat-box">
  <p className="stat-label">Plazo/Meses</p>
  <p className="stat-value">{deuda.plazo ? `${deuda.plazo} meses` : "-"}</p>
</div>

  <div className="stat-box">
    <p className="stat-label">{esDevolucion ? "Total devuelto" : "Total abonado/pagado"}</p>
    <p className="stat-value">{money(totalMostrado)}</p>
    {!esDevolucion && fin.engEnPagos && (
      <small style={{ color: "#64748b", display: "block", marginTop: 4 }}>
        Enganche: <strong>{money(fin.engancheCobrado)}</strong>
        {fin.mensualidadesCobradas > 0 && (
          <> · Mens: <strong>{money(fin.mensualidadesCobradas)}</strong></>
        )}
      </small>
    )}
  </div>

  {!esDevolucion && (
    <div className="stat-box">
      <p className="stat-label">Pagos realizados</p>
      <p className="stat-value">{money(pagosSinEnganche)}</p>
      <small style={{ color: "#64748b", display: "block", marginTop: 4 }}>
        Abonos sin enganche
      </small>
    </div>
  )}

  {!esDevolucion && Number(deuda?.mensualidad) > 0 && (
    <div className="stat-box">
      <p className="stat-label">Meses cubiertos</p>
      <p className="stat-value">
        {(pagosSinEnganche / Number(deuda.mensualidad)).toFixed(1)}
      </p>
      <small style={{ color: "#64748b", display: "block", marginTop: 4 }}>
        Abonos ÷ mensualidad
      </small>
    </div>
  )}

  <div className="stat-box stat-danger">
    <p className="stat-label">{esDevolucion ? "Pendiente por devolver" : "Saldo restante"}</p>
    <p className="stat-value">{money(saldoRestante)}</p>
  </div>

  <div className={`stat-box stat-estado ${estadoDeudaUI.className}`}>
    <p className="stat-label">Estado</p>
    <p className="stat-value">{estadoDeudaUI.label}</p>
    <small>{estadoDeudaUI.detalle}</small>
  </div>

  <div className="stat-box">
    <p className="stat-label">Próxima Fecha Pago</p>

    {!puedeEditarDeuda ? (
      <p className="stat-value">{formatDateOnlyMx(deuda.nextDueDate)}</p>
    ) : (
      <div style={{ display: "grid", gap: 6 }}>
        <input
          type="date"
          value={nextDueStr}
          onChange={(e) => setNextDueStr(e.target.value)}
          style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #ccc" }}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => saveNextDue(nextDueStr)} style={{ padding: "6px 10px", cursor: "pointer" }}>
            Guardar
          </button>
          <button
            onClick={() => saveNextDue("")}
            style={{ padding: "6px 10px", cursor: "pointer" }}
            title="Quitar la fecha"
          >
            Limpiar
          </button>
        </div>
      </div>
    )}
  </div>
</div>

      <div className="progress-container">
        <div className="progress-bar" style={{ width: `${Math.round(progreso)}%` }} />
      </div>

      <div className="escrituracion-status" style={{ margin: "20px 0 10px 0" }}>
        <label>
          <strong>Estatus de escrituración:</strong>
        </label>

        {puedeEditarDeuda ? (
          <select
            value={escrituracionStatus}
            onChange={(e) => handleChangeEscrituracionStatus(e.target.value)}
            style={{ marginLeft: 8, padding: "4px 8px", borderRadius: 6 }}
          >
            {escrituracionOpciones.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <span style={{ marginLeft: 8 }}>
            {escrituracionOpciones.find((opt) => opt.value === escrituracionStatus)?.label || "No definido"}
          </span>
        )}
      </div>

      {(puedeVerPromesas || puedeVerGestiones) && (
        <div className="operativo-cobranza">
          <div className="operativo-head">
            <div>
              <h3>Seguimiento operativo</h3>
              <p>Promesas de pago y gestiones recientes de esta deuda.</p>
            </div>
          </div>

          <div className="operativo-grid">
            {puedeVerPromesas && (
              <div className="operativo-card">
                <h4>Promesas de pago</h4>

                {(puedeCrearPromesas || (puedeEditarPromesas && promesaEditandoId)) && (
                  <form className="operativo-form" onSubmit={handleCrearPromesa}>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="Monto prometido"
                      value={promesaForm.montoPrometido}
                      onChange={(e) => setPromesaForm((prev) => ({ ...prev, montoPrometido: e.target.value }))}
                    />
                    <input
                      type="date"
                      value={promesaForm.fechaPromesa}
                      onChange={(e) => setPromesaForm((prev) => ({ ...prev, fechaPromesa: e.target.value }))}
                    />
                    <textarea
                      placeholder="Notas"
                      value={promesaForm.notas}
                      onChange={(e) => setPromesaForm((prev) => ({ ...prev, notas: e.target.value }))}
                    />
                    <button type="submit" disabled={savingOperativo}>
                      {savingOperativo
                        ? "Guardando..."
                        : promesaEditandoId
                        ? "Guardar cambios"
                        : "Registrar promesa"}
                    </button>
                    {promesaEditandoId && (
                      <button type="button" onClick={cancelarEdicionPromesa} disabled={savingOperativo}>
                        Cancelar edicion
                      </button>
                    )}
                  </form>
                )}

                <div className="operativo-list">
                  {promesasPago.slice(0, 5).map((p) => (
                    <div key={p.id} className={`operativo-item estado-${p.estado || "pendiente"}`}>
                      <strong>{money(p.montoPrometido)}</strong>
                      <span>{formatFecha(p.fechaPromesa, "Sin fecha")} · {safeRender(p.estado, "pendiente")}</span>
                      {p.notas && <small>{p.notas}</small>}
                      {puedeEditarPromesas && (
                        <div className="operativo-actions">
                          <button type="button" onClick={() => editarPromesa(p)}>
                            Editar
                          </button>
                          <button type="button" onClick={() => cambiarEstadoPromesa(p, "cumplida")}>
                            Cumplida
                          </button>
                          <button type="button" onClick={() => cambiarEstadoPromesa(p, "incumplida")}>
                            Incumplida
                          </button>
                          <button type="button" onClick={() => cambiarEstadoPromesa(p, "cancelada")}>
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {promesasPago.length === 0 && <p className="operativo-empty">Sin promesas registradas.</p>}
                </div>
              </div>
            )}

            {puedeVerGestiones && (
              <div className="operativo-card">
                <h4>Historial de gestion</h4>

                {puedeCrearGestiones && (
                  <form className="operativo-form" onSubmit={handleCrearGestion}>
                    <select
                      value={gestionForm.tipo}
                      onChange={(e) => setGestionForm((prev) => ({ ...prev, tipo: e.target.value }))}
                    >
                      <option value="llamada">Llamada</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="visita">Visita</option>
                      <option value="nota">Nota</option>
                      <option value="acuerdo">Acuerdo</option>
                    </select>
                    <select
                      value={gestionForm.resultado}
                      onChange={(e) => setGestionForm((prev) => ({ ...prev, resultado: e.target.value }))}
                    >
                      <option value="">Selecciona resultado</option>
                      <option value="contactado">Contactado</option>
                      <option value="sin_respuesta">Sin respuesta</option>
                      <option value="promesa_registrada">Promesa registrada</option>
                      <option value="requiere_seguimiento">Requiere seguimiento</option>
                      <option value="no_localizado">No localizado</option>
                    </select>
                    <textarea
                      placeholder="Comentario"
                      value={gestionForm.comentario}
                      onChange={(e) => setGestionForm((prev) => ({ ...prev, comentario: e.target.value }))}
                    />
                    <button type="submit" disabled={savingOperativo}>
                      {savingOperativo ? "Guardando..." : "Registrar gestion"}
                    </button>
                  </form>
                )}

                <div className="operativo-list">
                  {gestionesCobranza.slice(0, 5).map((g) => (
                    <div key={g.id} className="operativo-item">
                      <strong>{g.tipo || "gestion"}</strong>
                      <span>{safeRender(g.resultado, "-")} · {formatFecha(g.creadoEn, "Sin fecha")}</span>
                      {g.comentario && <small>{g.comentario}</small>}
                    </div>
                  ))}
                  {gestionesCobranza.length === 0 && <p className="operativo-empty">Sin gestiones registradas.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sprint 3.B: llamadas grabadas por el modulo Call Center Desktop.
          Oculto tras flag de ambiente (OFF en prod hasta el push próximo). */}
      {(puedeVerGestiones || puedeVerPromesas) && isCallCenterUiEnabled() && (
        <LlamadasDeuda deudaId={deudaId} />
      )}

      <Box sx={{ my: 2 }}>
        <Typography variant="h6">Documentos de deuda</Typography>

        {puedeEditarDeuda && (
        <Box
          sx={{
            border: arrastrando ? "2px dashed #1976d2" : "2px dashed #ccc",
            p: 2,
            borderRadius: 2,
            textAlign: "center",
            cursor: "pointer",
            my: 1,
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          {subiendo ? <CircularProgress /> : "Arrastra un archivo aquí o haz click para seleccionar"}
          <input type="file" style={{ display: "none" }} ref={inputRef} onChange={handleInputChange} />
        </Box>
        )}

        <List>
          {archivos.length === 0 && (
            <ListItem>
              <ListItemText primary="No hay archivos." />
            </ListItem>
          )}

          {archivos.map((a, i) => (
            <ListItem key={i} divider sx={{ py: 0.5 }}>
              <ListItemIcon>
                {tiposImagen.includes(a.ext) ? (
                  <img
                    src={a.url}
                    alt={a.nombre}
                    style={{ height: 40, width: 40, objectFit: "cover", borderRadius: 4 }}
                  />
                ) : tiposPDF.includes(a.ext) ? (
                  <PictureAsPdfIcon sx={{ fontSize: 40, color: "#b71c1c" }} />
                ) : (
                  <InsertDriveFileIcon sx={{ fontSize: 40, color: "#607d8b" }} />
                )}
              </ListItemIcon>

              <ListItemText
                primary={a.nombre}
                primaryTypographyProps={{
                  noWrap: true,
                  title: a.nombre,
                  sx: { maxWidth: { xs: 120, sm: 200, md: 280 } },
                }}
              />

              <ListItemSecondaryAction>
                <IconButton size="small" component="a" href={a.url} target="_blank">
                  <DownloadIcon />
                </IconButton>
                {puedeEditarDeuda && (
                  <IconButton size="small" onClick={() => eliminar(a.ref)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Box>

      {pagos.length > 0 && (
        <div className="payment-history">
          <h3>Historial de Pagos</h3>

          <div className="payment-history-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Fecha</th>
                <th scope="col">Monto</th>
                <th scope="col">Método</th>
                <th scope="col">Cobrador</th>
                <th scope="col">Referencia</th>
                <th scope="col">Caja</th>
                <th scope="col">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {pagos.map((p, i) => {
                const pagoId = getPagoId(p, i);
                const cajaIdDirecto = getPagoCajaId(p);
                const cajaLigada = cajaMovimientosPorPago.get(pagoId);
                const tieneCajaLigada = !!(cajaIdDirecto || cajaLigada);

                const esEnganche = String(p?.tipo || "").toLowerCase() === "enganche";
                return (
                <tr
                  key={p.pagoId || i}
                  style={esEnganche ? { background: "#f0fdf4" } : undefined}
                >
                  <td>{i + 1}</td>
                  <td>{formatFecha(p.fecha)}</td>
                  <td style={{ color: (p.cantidad || 0) < 0 ? "#b71c1c" : "inherit" }}>
                    {(p.cantidad || 0) < 0
                      ? `-${money(Math.abs(p.cantidad)).replace("$", "")}`
                      : money(p.cantidad)}

                    {esEnganche && (
                      <Chip size="small" label="Enganche" sx={{ ml: 1 }} color="success" />
                    )}
                    {p.tipo === "ajuste_devolucion" && <Chip size="small" label="Ajuste" sx={{ ml: 1 }} />}
                    {p.tipo === "devolucion_pago" && (
                      <Chip size="small" label="Devolución" sx={{ ml: 1 }} color="warning" />
                    )}
                  </td>
                  <td>{p.metodoEtiqueta || metodoPagoLabel(p.metodo) || "-"}</td>
                  <td>{p.cobradorNombre || p.cobradorEmail || p.cobradorId || "-"}</td>
                  <td>{p.referencia || "-"}</td>
                  <td>
                    {tieneCajaLigada ? (
                      <div style={{ display: "grid", gap: 4 }}>
                        <Chip size="small" label="Caja ligada" color="success" />
                        {deuda?.proyectoId ? (
                          <Link className="btn xs" to={`/app/proyectos/${deuda.proyectoId}/caja`}>
                            Ver caja
                          </Link>
                        ) : (
                          <small>{cajaIdDirecto || cajaLigada?.id}</small>
                        )}
                        {!cajaIdDirecto && cajaLigada?.id ? <small>Por ID de pago</small> : null}
                      </div>
                    ) : String(p?.tipo || "").toLowerCase() === "ajuste_devolucion" ? (
                      <Chip size="small" label="Sin caja (ajuste)" />
                    ) : (
                      <Chip size="small" label="Sin caja" color="warning" />
                    )}
                  </td>
                  <td>
                    <div className="payment-actions">
                      <IconButton
                        size="small"
                        onClick={() => setShowEvidPagoId(p.pagoId)}
                        title="Evidencias"
                        aria-label="Evidencias"
                      >
                        <AttachFileIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                      onClick={() =>
                        generarComprobantePagoPDF({
                          pago: p,
                          deuda,
                          cliente,
                          deudaId,
                        })
                      }
                      title="Recibo"
                        aria-label="Recibo"
                    >
                        <DescriptionIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                      onClick={() =>
                        generarTicketPagoPDF({
                          pago: p,
                          deuda,
                          cliente,
                          deudaId,
                        })
                      }
                      title="Ticket"
                        aria-label="Ticket"
                    >
                        <ConfirmationNumberIcon fontSize="small" />
                      </IconButton>

                      {puedeEditarPago && (
                        <IconButton
                          size="small"
                          onClick={() => abrirModalPago(i)}
                          title="Editar"
                          aria-label="Editar"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {responsable && (
        <div className="responsable-info">
          <h3>Responsable de Cobro</h3>
          <p>
            <strong>Nombre:</strong> {responsable.nombre} {responsable.apellidos}
          </p>

          {responsable.telefono && (
            <p>
              <strong>Teléfono:</strong> {responsable.telefono}
            </p>
          )}

          {responsable.email && (
            <p>
              <strong>Email:</strong> {responsable.email}
            </p>
          )}

          <Link to={`/app/cliente/${deuda.responsableId}`}>
            <button>Ver ficha</button>
          </Link>
        </div>
      )}

      <div className="detalle-acciones">
        {puedeDescargarPdf && (
        <button
          onClick={() =>
            generarEstadoCuentaPDF({
              deuda,
              cliente,
              clienteId,
              deudaId,
              promesasPago,
              gestionesCobranza,
              perfilCobranza,
            })
          }
          className="pdf-btn"
        >
          Descargar Estado de Cuenta
        </button>
        )}

        {puedeDescargarPdf && (
          <button onClick={abrirConstancia} className="pdf-btn">
            Descargar Constancia de Liquidación
          </button>
        )}

        {puedeEditarDeuda && (
          <button onClick={handleEditar} className="editar-btn">
            Editar
          </button>
        )}

        {puedeDesactivarDeuda && (
          <button onClick={toggleActivo} className="activar-desactivar-btn">
            {deuda.activo ? "Desactivar deuda" : "Reactivar deuda"}
          </button>
        )}

        {puedeEditarDeuda && (
          <button
            onClick={toggleGestionTelefonica}
            className="activar-desactivar-btn"
            title="Activa o desactiva las llamadas del call center (web y app) para esta cuenta"
            style={{
              background: deuda.gestionTelefonicaActiva === false ? "#fee2e2" : "#dcfce7",
              color: deuda.gestionTelefonicaActiva === false ? "#b91c1c" : "#166534",
              borderColor: deuda.gestionTelefonicaActiva === false ? "#fecaca" : "#bbf7d0",
            }}
          >
            {deuda.gestionTelefonicaActiva === false
              ? "🚫 Gestión telefónica: DESACTIVADA — Activar"
              : "📞 Gestión telefónica: activa — Desactivar (No llamar)"}
          </button>
        )}
        {puedeIniciarDevolucion && deuda.activo && !esDevolucion && (
          <button onClick={abrirDevolucion} className="devolucion-btn">
            Iniciar devolución
          </button>
        )}

        

        {puedeRegistrarPago && (
          <button onClick={() => setOpenSemiBulk(true)}>Registro semimasivo</button>
        )}

        <button onClick={() => navigate(`/app/cliente/${clienteId}/deudas`)}>← Volver</button>

        {puedeRegistrarPago && (
          <Link to={`/app/cliente/${clienteId}/deudas/${deudaId}/pago`}>
            <button>{esDevolucion ? "Registrar salida de devolución" : "Registrar Pago"}</button>
          </Link>
        )}
      </div>

      {modalPago.abierto && (
        <ModalPago
          isOpen={modalPago.abierto}
          onClose={cerrarModalPago}
          onSave={guardarPagoEditado}
          onDelete={puedeEliminarPago ? eliminarPago : undefined}
          pago={pagos[modalPago.index]}
          deudaId={deudaId}
          yaHayEnganche={fin.engEnPagos}
        />
      )}

      {openSemiBulk && (
        <ModalPagoSemimasivo
          isOpen={openSemiBulk}
          onClose={() => setOpenSemiBulk(false)}
          deuda={deuda}
          cliente={cliente}
          deudaId={deudaId}
        />
      )}

      {!!showEvidPagoId && (
        <PagoEvidenciaModal
          deudaId={deudaId}
          pagoId={showEvidPagoId}
          onClose={() => setShowEvidPagoId(null)}
          title="Evidencias del pago"
          basePath="deudas"
        />
      )}

      {modalRenegAbierto && (
        <ModalRenegociacion
          deudaId={deudaId}
          clienteNombre={
            cliente
              ? `${cliente.nombre || ""} ${cliente.apellidos || ""}`.trim()
              : ""
          }
          onClose={() => setModalRenegAbierto(false)}
        />
      )}

      {constanciaOpen && (
        <div
          className="constancia-overlay"
          onClick={() => setConstanciaOpen(false)}
        >
          <div
            className="constancia-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Constancia de Liquidación</h3>

            <label className="constancia-field">
              <span>Fecha (último pago del recibo)</span>
              <input
                type="date"
                value={constanciaFecha}
                onChange={(e) => setConstanciaFecha(e.target.value)}
              />
            </label>

            <label className="constancia-field">
              <span>Tratamiento</span>
              <select
                value={constanciaTrato}
                onChange={(e) => setConstanciaTrato(e.target.value)}
              >
                <option value="C.">C.</option>
                <option value="Sr.">Sr.</option>
                <option value="Sra.">Sra.</option>
              </select>
            </label>

            <div className="constancia-acciones">
              <button
                type="button"
                className="pdf-btn"
                onClick={generarConstancia}
              >
                Generar constancia
              </button>
              <button
                type="button"
                onClick={() => setConstanciaOpen(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetalleDeuda;
